import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import { areaForPath, areaLabel, canAccessArea, isClinicOwnerProfile } from '../../utils/routeAccess';

// Import UserRole type from AuthContext
type UserRole = 
  | 'admin' 
  | 'user'
  | 'professional'
  | 'secretary'
  | 'assistant'
  | 'intern'
  | 'patient';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  requiredPermission?: string;
  requiredPermissions?: string[];
  adminOnly?: boolean;
  professionalOnly?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requiredPermission,
  requiredPermissions,
  adminOnly = false,
  professionalOnly = false
}) => {
  const { currentUser, userRole, hasPermission, loading, userPermissions } = useAuthContext();
  const location = useLocation();

  // IMPORTANTE: Aguardar carregamento ANTES de fazer qualquer verificação
  // Isso garante que o AuthContext termine de restaurar a sessão do Firebase
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        flexDirection="column"
        gap={2}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="textSecondary">
          Carregando...
        </Typography>
      </Box>
    );
  }

  // Só redirecionar para login se realmente não houver usuário após carregamento completo
  if (!currentUser) {
    // Guarda a página pedida para voltar a ela depois do login (BUG-122).
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Permissão por área (financeiro, usuários, configurações, prontuário…), também para
  // quem digita o endereço direto. Dono da clínica e admin da plataforma sempre passam.
  const area = areaForPath(location.pathname);
  if (
    area &&
    !canAccessArea(area, {
      isClinicOwner: isClinicOwnerProfile(currentUser),
      permissions: userPermissions || [],
    })
  ) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Você não tem permissão para acessar <strong>{areaLabel(area)}</strong>. Peça ao responsável
          pela clínica para liberar o acesso em Configurações › Permissões.
        </Alert>
      </Box>
    );
  }

  // Verificar role específico
  if (requiredRole && userRole !== requiredRole) {
    return (
      <Alert severity="error">
        Você não tem permissão para acessar esta página.
      </Alert>
    );
  }

  // Verificar se é admin apenas
  if (adminOnly && userRole !== 'admin') {
    return (
      <Alert severity="error">
        Acesso restrito a administradores.
      </Alert>
    );
  }

  // Verificar se é profissional apenas
  if (professionalOnly && userRole !== 'professional') {
    return (
      <Alert severity="error">
        Acesso restrito a profissionais.
      </Alert>
    );
  }

  // Verificar permissão específica
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Alert severity="error">
        Você não tem permissão para acessar esta página.
        <br />
        Permissão necessária: <strong>{requiredPermission}</strong>
      </Alert>
    );
  }

  // Verificar múltiplas permissões
  if (requiredPermissions && requiredPermissions.length > 0) {
    const missingPermissions = requiredPermissions.filter(permission => !hasPermission(permission));
    if (missingPermissions.length > 0) {
      return (
        <Alert severity="error">
          Você não tem permissão para acessar esta página.
          <br />
          Permissões necessárias: <strong>{missingPermissions.join(', ')}</strong>
        </Alert>
      );
    }
  }

  return <>{children}</>;
}; 